import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useGlobalQuery } from 'state/query-client/hooks';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useOnboardingStore } from 'state/store/onboarding/use-onboarding-store';
import { resolveAddressForm } from '../address-form';
import {
  getConversationPreferences,
  isPreferencesForbidden,
  onConversationPreferencesSaved,
} from '../services/conversation-preferences-service';

export const CONVERSATION_PREFERENCES_QUERY_KEY = ['conversation-preferences'];

/**
 * The signed-in user's conversation preferences. Every successful write, from any screen or
 * from onboarding, invalidates it, so later screens show the new values without a reload.
 *
 * @example
 * const { data } = useConversationPreferences();
 */
export function useConversationPreferences({ enabled = true }: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userId = useCommonStore((state) => state.user?.id);

  useEffect(
    () =>
      onConversationPreferencesSaved(() => {
        queryClient.invalidateQueries({ queryKey: CONVERSATION_PREFERENCES_QUERY_KEY });
      }),
    [queryClient]
  );

  return useGlobalQuery({
    queryKey: [...CONVERSATION_PREFERENCES_QUERY_KEY, userId],
    queryFn: getConversationPreferences,
    staleTime: 60 * 1000,
    // Another tab or the Klax app may have changed them; a stale copy is read again on focus.
    refetchOnWindowFocus: true,
    enabled: enabled && isAuthenticated && !!userId,
    // A refused session stays refused; anything else gets one more try.
    retry: (failureCount, error) => !isPreferencesForbidden(error) && failureCount < 1,
  });
}

/** How UI copy addresses the user: the saved form, else the onboarding answer, else formal. */
export function useAddressForm({ enabled = true }: { enabled?: boolean } = {}) {
  const { data } = useConversationPreferences({ enabled });
  const userId = useCommonStore((state) => state.user?.id);
  const localForm = useOnboardingStore((state) =>
    userId ? state.progressByUser[userId]?.addressForm : undefined
  );
  return resolveAddressForm(data?.addressForm, localForm);
}
